import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Plus, X, Pencil, Trash2, ArrowLeft, Search } from 'lucide-react';
import { NairaInput } from '../../lib/nairaInput';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const statusColors = { available: 'bg-green-100 text-green-800', reserved: 'bg-amber-100 text-amber-800', sold: 'bg-red-100 text-red-800' };
const statusLabels = { available: 'Available', reserved: 'Reserved', sold: 'Sold' };
const formatNaira = (amount) => !amount && amount !== 0 ? '--' : new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);

const BOPlots = () => {
  const { role } = useBOAuth();
  const [estateList, setEstateList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEstateForm, setShowEstateForm] = useState(false);
  const [editEstate, setEditEstate] = useState(null);
  const [estateForm, setEstateForm] = useState({ name: '', location: '', num_blocks: '', plot_size: '', price_per_plot: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  // Estate detail view
  const [selectedEstate, setSelectedEstate] = useState(null);
  const [estatePlots, setEstatePlots] = useState([]);
  const [plotSearch, setPlotSearch] = useState('');
  const [filterBlock, setFilterBlock] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Block plot generation
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({ block_name: '', plot_ranges: '', plot_size: '', price: '' });

  // Confirmation dialogs for destructive deletes
  const [confirmDel, setConfirmDel] = useState({ open: false, type: null, payload: null });

  const isReadOnly = role === 'accountant';

  useEffect(() => { fetchEstates(); }, []);

  // Refresh estate counts whenever the tab/window regains focus or becomes visible.
  // Ensures sold/available numbers stay in sync after a sale is recorded on another tab.
  useEffect(() => {
    const onFocus = () => fetchEstates();
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchEstates(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const fetchEstates = async () => {
    try {
      const res = await boAPI.getEstates();
      setEstateList(res.data.estates || []);
      // If we're viewing an estate detail, keep selectedEstate in sync so newly-added
      // blocks show up immediately without going back to the estate list.
      if (selectedEstate) {
        const fresh = (res.data.estates || []).find(e => e.id === selectedEstate.id);
        if (fresh) setSelectedEstate(fresh);
      }
    } catch (e) { toast.error('Failed to load estates'); }
    finally { setLoading(false); }
  };

  const fetchEstatePlots = async (estateName) => {
    try {
      const res = await boAPI.getPlots({ estate: estateName });
      setEstatePlots(res.data.plots || []);
    } catch (e) { toast.error('Failed to load plots'); }
  };

  const handleOpenEstate = (est) => {
    setSelectedEstate(est);
    setFilterBlock(''); setFilterStatus(''); setPlotSearch('');
    fetchEstatePlots(est.name);
  };

  const handleEstateSubmit = async (e) => {
    e.preventDefault();
    if (!estateForm.name.trim()) { toast.error('Estate name is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...estateForm, num_blocks: parseInt(estateForm.num_blocks) || null, price_per_plot: parseFloat(estateForm.price_per_plot) || null };
      if (editEstate) {
        await boAPI.updateEstate(editEstate.id, payload);
        toast.success('Estate updated');
      } else {
        await boAPI.createEstate(payload);
        toast.success('Estate created');
      }
      setShowEstateForm(false); setEditEstate(null);
      setEstateForm({ name: '', location: '', num_blocks: '', plot_size: '', price_per_plot: '', description: '' });
      fetchEstates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save estate'); }
    finally { setSubmitting(false); }
  };

  const handleEstateEdit = (est) => {
    setEditEstate(est);
    setEstateForm({ name: est.name || '', location: est.location || '', num_blocks: est.num_blocks || '', plot_size: est.plot_size || '', price_per_plot: est.price_per_plot || '', description: est.description || '' });
    setShowEstateForm(true);
  };

  const handleEstateDelete = async (est) => {
    setConfirmDel({ open: true, type: 'estate', payload: est });
  };

  const handlePlotDelete = (p) => {
    setConfirmDel({ open: true, type: 'plot', payload: p });
  };

  const handleBlockDelete = (block) => {
    setConfirmDel({ open: true, type: 'block', payload: { estate_name: selectedEstate.name, block_name: block } });
  };

  const performDelete = async () => {
    const { type, payload } = confirmDel;
    try {
      if (type === 'estate') {
        const r = await boAPI.deleteEstate(payload.id);
        toast.success(r.data?.message || 'Estate deleted');
        fetchEstates();
      } else if (type === 'plot') {
        const r = await boAPI.deletePlot(payload.id);
        toast.success(r.data?.message || 'Plot deleted');
        if (selectedEstate) fetchEstatePlots(selectedEstate.name);
        fetchEstates();
      } else if (type === 'block') {
        const r = await boAPI.deleteEstateBlock(payload.estate_name, payload.block_name);
        toast.success(r.data?.message || 'Block deleted');
        if (selectedEstate) fetchEstatePlots(selectedEstate.name);
        fetchEstates();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setConfirmDel({ open: false, type: null, payload: null });
    }
  };

  const handleCreatePhase = async (est) => {
    try {
      const res = await boAPI.createEstatePhase(est.id);
      toast.success(res.data.message || 'New phase created');
      fetchEstates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create phase'); }
  };

  // Open the "Add Plots to Block" form. If blockName is empty, suggest the next sequential block name.
  const openBlockForm = (blockName) => {
    const existingBlocks = selectedEstate?.blocks || [];
    let suggested = blockName;
    if (!suggested) {
      // find highest "Block N" and suggest N+1; otherwise just "Block 1"
      const nums = existingBlocks
        .map(b => {
          const m = /(?:Block|block)\s*(\d+)/.exec(b);
          return m ? parseInt(m[1], 10) : null;
        })
        .filter(n => n !== null);
      const next = nums.length ? Math.max(...nums) + 1 : 1;
      suggested = `Block ${next}`;
    }
    setShowBlockForm(true);
    setBlockForm({
      block_name: suggested,
      plot_ranges: '',
      plot_size: selectedEstate?.plot_size || '',
      price: selectedEstate?.price_per_plot || '',
    });
  };

  const handleGenerateBlockPlots = async (e) => {
    e.preventDefault();
    if (!blockForm.block_name || !blockForm.plot_ranges) { toast.error('Block name and plot ranges required'); return; }
    setSubmitting(true);
    try {
      const res = await boAPI.generateBlockPlots({
        estate_name: selectedEstate.name,
        block_name: blockForm.block_name,
        plot_ranges: blockForm.plot_ranges,
        plot_size: blockForm.plot_size || selectedEstate.plot_size || null,
        price: parseFloat(blockForm.price) || selectedEstate.price_per_plot || 0,
      });
      toast.success(res.data.message);
      setShowBlockForm(false); setBlockForm({ block_name: '', plot_ranges: '', plot_size: '', price: '' });
      fetchEstatePlots(selectedEstate.name); fetchEstates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate plots'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  // =========== ESTATE DETAIL VIEW ===========
  if (selectedEstate) {
    const blocks = selectedEstate.blocks || [];
    let filtered = estatePlots;
    if (filterBlock) filtered = filtered.filter(p => p.location_block === filterBlock);
    if (filterStatus) filtered = filtered.filter(p => p.availability_status === filterStatus);
    if (plotSearch) filtered = filtered.filter(p => p.plot_number?.toLowerCase().includes(plotSearch.toLowerCase()));

    return (
      <BOLayout>
        <div className="space-y-5" data-testid="estate-detail-view">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedEstate(null)} className="p-2 rounded-lg hover:bg-neutral-100" data-testid="back-to-estates"><ArrowLeft className="h-5 w-5" /></button>
              <div>
                <h1 className="text-2xl font-serif font-bold text-neutral-900">{selectedEstate.name}</h1>
                <p className="text-neutral-500 text-sm">{selectedEstate.location || ''} - {estatePlots.length} plots across {blocks.length} blocks</p>
              </div>
            </div>
            {!isReadOnly && (
              <Button onClick={() => openBlockForm('')} className="rounded-xl bg-green-600 hover:bg-green-700" data-testid="add-block-plots-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Plots to Block
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            <Card className="border-green-200 shadow-card"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-700">{estatePlots.filter(p => p.availability_status === 'available').length}</p><p className="text-xs text-neutral-500">Available</p></CardContent></Card>
            <Card className="border-red-200 shadow-card"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-600">{estatePlots.filter(p => p.availability_status === 'sold').length}</p><p className="text-xs text-neutral-500">Sold</p></CardContent></Card>
            <Card className="border-neutral-border shadow-card"><CardContent className="p-3 text-center"><p className="text-xl font-bold">{estatePlots.length}</p><p className="text-xs text-neutral-500">Total</p></CardContent></Card>
            <Card className="border-primary-200 shadow-card hidden sm:block"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-primary-700">{blocks.length}</p><p className="text-xs text-neutral-500">Blocks</p></CardContent></Card>
          </div>

          {/* Block Plot Generation Form */}
          {showBlockForm && (
            <Card className="border-green-200 shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle>Add Plots to Block</CardTitle><CardDescription>Use ranges like "1-5, 40-45" for fast entry</CardDescription></div>
                <button onClick={() => setShowBlockForm(false)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGenerateBlockPlots} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Block Name *</Label><Input value={blockForm.block_name} onChange={(e) => setBlockForm({ ...blockForm, block_name: e.target.value })} placeholder="e.g., Block 1" className="mt-1.5 rounded-xl" data-testid="block-name-input" /></div>
                  <div><Label>Price per Plot (Naira)</Label><NairaInput value={blockForm.price} onChange={(v) => setBlockForm({ ...blockForm, price: v })} className="mt-1.5 rounded-xl" /></div>
                  <div className="sm:col-span-2">
                    <Label>Plot Ranges *</Label>
                    <Input value={blockForm.plot_ranges} onChange={(e) => setBlockForm({ ...blockForm, plot_ranges: e.target.value })} placeholder='e.g., "1-5, 12a, 14b-14d, 100-110"' className="mt-1.5 rounded-xl" data-testid="plot-ranges-input" />
                    <p className="text-xs text-neutral-400 mt-1">Use commas to separate. Numbers ("1-5") and letter-suffixed plots ("12a") supported — useful for surveyor-correction plots that share a number.</p>
                  </div>
                  <div className="sm:col-span-2 flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowBlockForm(false)} className="rounded-xl">Cancel</Button>
                    <Button type="submit" disabled={submitting} className="rounded-xl bg-green-600 hover:bg-green-700" data-testid="generate-plots-submit">{submitting ? 'Generating...' : 'Generate Plots'}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {blocks.length > 0 && (
              <select value={filterBlock} onChange={(e) => setFilterBlock(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-block">
                <option value="">All Blocks</option>
                {blocks.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
              <option value="">All Statuses</option>
              <option value="available">Available</option>
              <option value="sold">Sold</option>
              <option value="reserved">Reserved</option>
            </select>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input value={plotSearch} onChange={(e) => setPlotSearch(e.target.value)} placeholder="Search plot number..." className="pl-10 rounded-xl text-sm" />
            </div>
          </div>

          {/* Plots grouped by block */}
          {blocks.length > 0 ? blocks.filter(b => !filterBlock || b === filterBlock).map(block => {
            const blockPlots = filtered.filter(p => p.location_block === block);
            if (blockPlots.length === 0 && filterStatus) return null;
            return (
              <Card key={block} className="border-neutral-border shadow-card">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center justify-between gap-3">
                    <span>{block}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-normal text-neutral-500">{blockPlots.length} plots</span>
                      {!isReadOnly && (
                        <>
                          <button onClick={() => openBlockForm(block)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1 font-medium" title={`Add more plots to ${block}`} data-testid={`add-plots-to-${block}`}>
                            <Plus className="h-3.5 w-3.5" /> Add plots
                          </button>
                          <button onClick={() => handleBlockDelete(block)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-normal" title={`Delete entire block ${block}`} data-testid={`delete-block-${block}`}>
                            <Trash2 className="h-3.5 w-3.5" /> Delete block
                          </button>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {blockPlots.map(p => (
                      <div key={p.id} className={`relative p-2 rounded-xl border text-center text-sm ${p.availability_status === 'sold' ? (p.allocation_type === 'firm' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-amber-50 border-amber-300 text-amber-800') : p.availability_status === 'reserved' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid={`plot-${p.id}`}>
                        <p className="font-semibold text-xs">{p.plot_number}</p>
                        <p className="text-[10px] capitalize">{p.availability_status === 'sold' && p.allocation_type ? (p.allocation_type === 'firm' ? 'Sold · Firm' : 'Sold · Provisional') : statusLabels[p.availability_status]}</p>
                        {!isReadOnly && (
                          <button onClick={() => handlePlotDelete(p)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-red-300 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center shadow-sm" title="Delete this plot" data-testid={`delete-plot-${p.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {blockPlots.length === 0 && <p className="col-span-full text-center text-neutral-400 py-4 text-sm">No plots in this block yet</p>}
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <Card className="border-neutral-border shadow-card">
              <CardContent className="py-8 text-center text-neutral-500">
                No blocks created yet. Click "Add Plots to Block" to get started.
              </CardContent>
            </Card>
          )}

          {/* Unblocked plots */}
          {(() => {
            const unblockedPlots = filtered.filter(p => !p.location_block);
            if (unblockedPlots.length === 0) return null;
            return (
              <Card className="border-neutral-border shadow-card">
                <CardHeader className="py-3"><CardTitle className="text-base">Unassigned to Block ({unblockedPlots.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {unblockedPlots.map(p => (
                      <div key={p.id} className={`relative p-2 rounded-xl border text-center text-sm ${p.availability_status === 'sold' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                        <p className="font-semibold text-xs">{p.plot_number}</p>
                        <p className="text-[10px] capitalize">{statusLabels[p.availability_status]}</p>
                        {!isReadOnly && (
                          <button onClick={() => handlePlotDelete(p)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-red-300 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center shadow-sm" title="Delete this plot" data-testid={`delete-plot-${p.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>
        <ConfirmDialog
          open={confirmDel.open}
          onOpenChange={(o) => !o && setConfirmDel({ open: false, type: null, payload: null })}
          title={confirmDel.type === 'estate'
            ? `Delete entire estate "${confirmDel.payload?.name}"?`
            : confirmDel.type === 'block'
            ? `Delete entire ${confirmDel.payload?.block_name}?`
            : confirmDel.type === 'plot'
            ? `Delete plot ${confirmDel.payload?.plot_number}?`
            : 'Delete?'}
          description={confirmDel.type === 'estate'
            ? 'This will permanently remove the estate and ALL of its plots, blocks, sales, payments and development fees. Clients who held plots will be unassigned. This cannot be undone.'
            : confirmDel.type === 'block'
            ? `Every plot in ${confirmDel.payload?.block_name} (and any related sales, payments and dev-fees) will be permanently removed.`
            : confirmDel.type === 'plot'
            ? 'This plot and any related sales, payments and dev-fees will be permanently removed. If a client held it, they will be unassigned.'
            : ''}
          confirmLabel="Yes, delete"
          onConfirm={performDelete}
        />
      </BOLayout>
    );
  }

  // =========== ESTATES LIST VIEW ===========
  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-plots-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Plot Management</h1>
            <p className="text-neutral-500 mt-1">{estateList.length} estate{estateList.length !== 1 ? 's' : ''}</p>
          </div>
          {!isReadOnly && (
            <Button onClick={() => { setEditEstate(null); setEstateForm({ name: '', location: '', num_blocks: '', plot_size: '', price_per_plot: '', description: '' }); setShowEstateForm(true); }} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="create-estate-btn">
              <Plus className="h-4 w-4 mr-2" /> Create Estate
            </Button>
          )}
        </div>

        {/* Create/Edit Estate Form */}
        {showEstateForm && (
          <Card className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>{editEstate ? 'Edit Estate' : 'Create New Estate'}</CardTitle><CardDescription>Enter estate details</CardDescription></div>
              <button onClick={() => { setShowEstateForm(false); setEditEstate(null); }} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEstateSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Estate Name *</Label><Input value={estateForm.name} onChange={(e) => setEstateForm({ ...estateForm, name: e.target.value })} placeholder="e.g., City of Heaven Estate" className="mt-1.5 rounded-xl" data-testid="estate-form-name" /></div>
                <div><Label>Location</Label><Input value={estateForm.location} onChange={(e) => setEstateForm({ ...estateForm, location: e.target.value })} placeholder="e.g., Asaba, Delta State" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Number of Blocks</Label><Input type="number" value={estateForm.num_blocks} onChange={(e) => setEstateForm({ ...estateForm, num_blocks: e.target.value })} placeholder="e.g., 5" className="mt-1.5 rounded-xl" data-testid="estate-form-blocks" /></div>
                <div><Label>Default Plot Size</Label><Input value={estateForm.plot_size} onChange={(e) => setEstateForm({ ...estateForm, plot_size: e.target.value })} placeholder="e.g., 464 sqm" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Default Price per Plot</Label><NairaInput value={estateForm.price_per_plot} onChange={(v) => setEstateForm({ ...estateForm, price_per_plot: v })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Description</Label><Input value={estateForm.description} onChange={(e) => setEstateForm({ ...estateForm, description: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div className="sm:col-span-2 lg:col-span-3 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowEstateForm(false); setEditEstate(null); }} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="estate-form-submit">{submitting ? 'Saving...' : editEstate ? 'Update Estate' : 'Create Estate'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Estates Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {estateList.map((est) => (
            <Card key={est.id} className={`border shadow-card transition-all cursor-pointer hover:shadow-lg ${est.status === 'sold_out' ? 'border-red-200' : 'border-green-200'}`} onClick={() => handleOpenEstate(est)} data-testid={`estate-card-${est.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-neutral-900">{est.name}</h3>
                    {est.location && <p className="text-xs text-neutral-500">{est.location}</p>}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${est.status === 'sold_out' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {est.status === 'sold_out' ? 'Sold Out' : 'Available'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="bg-white rounded-lg p-2 border"><p className="text-lg font-bold text-green-700">{est.available_plots}</p><p className="text-[10px] text-neutral-500">Available</p></div>
                  <div className="bg-white rounded-lg p-2 border"><p className="text-lg font-bold text-red-600">{est.sold_plots}</p><p className="text-[10px] text-neutral-500">Sold</p></div>
                  <div className="bg-white rounded-lg p-2 border"><p className="text-lg font-bold text-neutral-700">{est.total_plots_actual}</p><p className="text-[10px] text-neutral-500">Total</p></div>
                </div>
                {est.blocks && est.blocks.length > 0 && (
                  <div className="mb-2">
                    <div className="flex flex-wrap gap-1">{est.blocks.map(b => <span key={b} className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-[10px]">{b}</span>)}</div>
                  </div>
                )}
                {est.price_per_plot && <p className="text-xs text-neutral-500">{formatNaira(est.price_per_plot)} per plot</p>}
                {!isReadOnly && (
                  <div className="flex gap-2 mt-3 pt-3 border-t flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleEstateEdit(est)} className="text-xs text-neutral-600 hover:text-primary-700 flex items-center gap-1" data-testid={`estate-edit-${est.id}`}><Pencil className="h-3 w-3" /> Edit</button>
                    <button onClick={() => handleCreatePhase(est)} className="text-xs text-primary-700 hover:text-primary-900 flex items-center gap-1 font-medium" data-testid={`estate-create-phase-${est.id}`}><Plus className="h-3 w-3" /> Create Phase</button>
                    <button onClick={() => handleEstateDelete(est)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid={`estate-delete-${est.id}`}><Trash2 className="h-3 w-3" /> Delete</button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {estateList.length === 0 && <div className="sm:col-span-2 lg:col-span-3 text-center py-12 text-neutral-500">No estates yet. Click "Create Estate" to get started.</div>}
        </div>
      </div>
      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(o) => !o && setConfirmDel({ open: false, type: null, payload: null })}
        title={confirmDel.type === 'estate' ? `Delete entire estate "${confirmDel.payload?.name}"?` : 'Delete?'}
        description={confirmDel.type === 'estate'
          ? 'This will permanently remove the estate and ALL of its plots, blocks, sales, payments and development fees. Clients who held plots will be unassigned. This cannot be undone.'
          : ''}
        confirmLabel="Yes, delete"
        onConfirm={performDelete}
      />
    </BOLayout>
  );
};

export default BOPlots;
